import { DashboardService } from 'data/services';
import {
  GroupServiceInterface,
  CourseServiceInterface,
  CurricularUnitServiceInterface,
  ActivityReportInternshipServiceInterface,
  HandleReportsAnalyticalActivitiesByStudentServiceInterface,
  OrganizationServiceInterface,
  ProfessionalServiceInterface,
  ReportServiceInterface,
  SemesterServiceInterface,
  InternshipLocationServiceInterface,
  UsersServiceInterface,
  StudentServiceInterface,
  ProfessionListServiceInterface,
  AuthenticationServiceInterface,
  PushNotificationServiceInterface,
  TceSummaryServiceInterface,
  CancellationReasonsServiceInterface,
  CommonUtilsServiceInterface,
  NotificationsServiceInterface,
} from 'domain/services';
import { LocationsServiceInterface } from 'domain/services/locations';
import { HandleActivitiesSummarizedByStudentUseCaseInterface } from 'domain/usecases';
import { makeAuthSignOutService } from 'main/factories/services/authentication/auth.service.factory';
import { makeCancellationReasonsService } from 'main/factories/services/cancellation-reasons/cancellation-reasons.service.factory';
import { makeCommonUtilsService } from 'main/factories/services/common-utils/common-utils.service.factory';
import { makeCourseService } from 'main/factories/services/courses/course.service.factory';
import { makeCurricularUnitService } from 'main/factories/services/curricular-units/curricular-unit.service.factory';
import { makeDashboardService } from 'main/factories/services/dashboard/dashboard.service.factory';
import { makeGroupService } from 'main/factories/services/groups/group.service.factory';
import { makeInternshipLocationService } from 'main/factories/services/interhship-location/internship-location.service.factory';
import { makeLocationsService } from 'main/factories/services/locations/locations.service.factory';
import { makeNotificationsService } from 'main/factories/services/notifications';
import { makeOrganizationService } from 'main/factories/services/organizations/organization.service.factory';
import { makeStudentService } from 'main/factories/services/persons/students/student.service.factory';
import { makeProfession } from 'main/factories/services/profession/profession.service.factory';
import { makeProfessionalService } from 'main/factories/services/professionals/professional.service.factory';
import { makePushNotificationService } from 'main/factories/services/push-notification';
import { makeActivityReportInternshipLocationService } from 'main/factories/services/reports/activity-report-internship-location';
import { makeHandleReportsAnalytical } from 'main/factories/services/reports/handle.reports.analytical.activities.students';
import { makeReportService } from 'main/factories/services/reports/report.service.factory';
import { makeSemesterService } from 'main/factories/services/semesters/semester.service.factory';
import { makeTceSummaryService } from 'main/factories/services/tce-summary/tce-summary.service.factory';
import { makeUsersService } from 'main/factories/services/users/users.service.factory';
import { makeHandleActivitiesSummarizedByStudent } from 'main/factories/usecases/reports/remote-handle-activities-summarized-by-student-factory';
import { WithChildren } from 'presentation/config/helpers/react18MigrationHelpers';
import { FC, createContext, useContext, useState } from 'react';

type ServiceContextProps = {
  courseService: CourseServiceInterface;
  organizationService: OrganizationServiceInterface;
  curricularUnitService: CurricularUnitServiceInterface;
  getActivityReportInternshipInterface: ActivityReportInternshipServiceInterface;
  semesterService: SemesterServiceInterface;
  groupService: GroupServiceInterface;
  dashboardService: DashboardService;
  reportService: ReportServiceInterface;
  reportsAnalyticalActivities: HandleReportsAnalyticalActivitiesByStudentServiceInterface;
  studentService: StudentServiceInterface;
  professionalService: ProfessionalServiceInterface;
  internshipLocationService: InternshipLocationServiceInterface;
  activitiesSummarizedByStudentService: HandleActivitiesSummarizedByStudentUseCaseInterface;
  usersService: UsersServiceInterface;
  locationsService: LocationsServiceInterface;
  professionService: ProfessionListServiceInterface;
  authenticationService: AuthenticationServiceInterface;
  pushNotificationService: PushNotificationServiceInterface;
  tceSummaryService?: TceSummaryServiceInterface;
  cancellationReasonsService: CancellationReasonsServiceInterface;
  commonUtilsService: CommonUtilsServiceInterface;
  notificationsService: NotificationsServiceInterface;
};

const initServiceContextPropsState = {
  groupService: makeGroupService(),
  courseService: makeCourseService(),
  semesterService: makeSemesterService(),
  dashboardService: makeDashboardService(),
  organizationService: makeOrganizationService(),
  curricularUnitService: makeCurricularUnitService(),
  getActivityReportInternshipInterface:
    makeActivityReportInternshipLocationService(),
  reportService: makeReportService(),
  reportsAnalyticalActivities: makeHandleReportsAnalytical(),
  studentService: makeStudentService(),
  professionalService: makeProfessionalService(),
  internshipLocationService: makeInternshipLocationService(),
  activitiesSummarizedByStudentService:
    makeHandleActivitiesSummarizedByStudent(),
  usersService: makeUsersService(),
  locationsService: makeLocationsService(),
  professionService: makeProfession(),
  authenticationService: makeAuthSignOutService(),
  pushNotificationService: makePushNotificationService(),
  tceSummaryService: makeTceSummaryService(),
  cancellationReasonsService: makeCancellationReasonsService(),
  commonUtilsService: makeCommonUtilsService(),
  notificationsService: makeNotificationsService(),
};

const ServiceContext = createContext<ServiceContextProps>(
  initServiceContextPropsState,
);

const useService = () => {
  return useContext(ServiceContext);
};

const ServiceProvider: FC<WithChildren> = ({ children }) => {
  const [serviceContextProps] = useState(initServiceContextPropsState);
  return (
    <ServiceContext.Provider value={serviceContextProps}>
      {children}
    </ServiceContext.Provider>
  );
};

export { ServiceProvider, useService };
